import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Pencil, Search, X, ChevronLeft, ChevronRight, MoreVertical } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { taxMasterApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { TaxMasterListItem, CreateTaxMasterRequest, UpdateTaxMasterRequest } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const ALL_STATUSES = '__all__'
const ALL_TYPES = '__all__'

interface TaxFilters {
  searchTerm: string
  status: string
  type: string
}

export default function TaxMasterPage() {
  const { t } = useTranslation()
  const { canAddEdit } = usePermissions()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingTax, setEditingTax] = useState<TaxMasterListItem | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [filters, setFilters] = useState<TaxFilters>({
    searchTerm: '',
    status: ALL_STATUSES,
    type: ALL_TYPES,
  })
  const [formData, setFormData] = useState<CreateTaxMasterRequest>({
    tax_name: '',
    tax_rate: 0,
    remarks: '',
    is_gst: false,
    cgst_rate: 0,
    sgst_rate: 0,
    igst_rate: 0,
    utgst_rate: 0,
  })

  const queryClient = useQueryClient()

  const { data: taxes = [], isLoading } = useQuery({
    queryKey: ['taxes'],
    queryFn: () => taxMasterApi.getAll(),
  })

  // Filter taxes based on filters
  const filteredTaxes = useMemo(() => {
    return taxes.filter(tax => {
      // Search term filter
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase()
        const matchesName = tax.tax_name.toLowerCase().includes(searchLower)
        if (!matchesName) return false
      }
      // Status filter
      if (filters.status !== ALL_STATUSES) {
        const isActive = filters.status === 'active'
        if (tax.is_active !== isActive) return false
      }
      // Type filter (GST vs Standard)
      if (filters.type !== ALL_TYPES) {
        const isGst = filters.type === 'gst'
        if (tax.is_gst !== isGst) return false
      }
      return true
    })
  }, [taxes, filters])

  // Pagination calculations
  const totalRecords = filteredTaxes.length
  const totalPages = Math.ceil(totalRecords / pageSize)
  const paginatedTaxes = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return filteredTaxes.slice(startIndex, startIndex + pageSize)
  }, [filteredTaxes, currentPage, pageSize])

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalRecords)

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setCurrentPage(1)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1)
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1)
  }

  const clearFilters = () => {
    setFilters({ searchTerm: '', status: ALL_STATUSES, type: ALL_TYPES })
    setCurrentPage(1)
  }

  const hasActiveFilters = !!(filters.searchTerm || filters.status !== ALL_STATUSES || filters.type !== ALL_TYPES)

  const createMutation = useMutation({
    mutationFn: taxMasterApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxes'] })
      setIsDialogOpen(false)
      resetForm()
      toast.success(t('masters.tax.taxCreated'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('masters.tax.failedToCreate')
      toast.error(errorMessage)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTaxMasterRequest }) =>
      taxMasterApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxes'] })
      setIsDialogOpen(false)
      setEditingTax(null)
      resetForm()
      toast.success(t('masters.tax.taxUpdated'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('masters.tax.failedToUpdate')
      toast.error(errorMessage)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: taxMasterApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxes'] })
      toast.success(t('masters.tax.taxDeleted'))
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || t('masters.tax.failedToDelete')
      toast.error(errorMessage)
    },
  })

  const resetForm = () => {
    setFormData({
      tax_name: '',
      tax_rate: 0,
      remarks: '',
      is_gst: false,
      cgst_rate: 0,
      sgst_rate: 0,
      igst_rate: 0,
      utgst_rate: 0,
    })
    setEditingTax(null)
  }

  const handleEdit = (tax: TaxMasterListItem) => {
    setEditingTax(tax)
    setFormData({
      tax_name: tax.tax_name,
      tax_rate: tax.tax_rate,
      remarks: tax.remarks || '',
      is_gst: tax.is_gst,
      cgst_rate: tax.cgst_rate,
      sgst_rate: tax.sgst_rate,
      igst_rate: tax.igst_rate,
      utgst_rate: tax.utgst_rate,
    })
    setIsDialogOpen(true)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (editingTax) {
      updateMutation.mutate({
        id: editingTax.tax_id,
        data: formData,
      })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleOpenChange = (open: boolean) => {
    setIsDialogOpen(open)
    if (!open) {
      resetForm()
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{t('masters.tax.title')}</h1>
          <p className="text-muted-foreground">
            {t('masters.tax.subtitle')}
          </p>
        </div>

        {canAddEdit('TAX_MASTER') && (
          <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                {t('masters.tax.addTax')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
            <form onSubmit={handleSubmit}>
              <DialogHeader>
                <DialogTitle>{editingTax ? t('masters.tax.editTax') : t('masters.tax.addTax')}</DialogTitle>
                <DialogDescription>
                  {editingTax ? t('masters.tax.updateTaxConfig') : t('masters.tax.createNewTaxConfig')}
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="tax_name">{t('masters.tax.taxName')} *</Label>
                  <Input
                    id="tax_name"
                    value={formData.tax_name}
                    onChange={(e) => setFormData({ ...formData, tax_name: e.target.value })}
                    placeholder="e.g., GST 18%, VAT 5%"
                    required
                  />
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    id="is_gst"
                    checked={formData.is_gst}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_gst: checked })}
                  />
                  <Label htmlFor="is_gst">{t('masters.tax.gstTax')}</Label>
                </div>

                {!formData.is_gst ? (
                  <div className="grid gap-2">
                    <Label htmlFor="tax_rate">{t('masters.tax.taxRate')} (%)</Label>
                    <Input
                      id="tax_rate"
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={formData.tax_rate || ''}
                      onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) || 0 })}
                      placeholder="e.g., 5"
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="cgst_rate">{t('masters.tax.cgstRate')}</Label>
                      <Input
                        id="cgst_rate"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.cgst_rate || ''}
                        onChange={(e) => setFormData({ ...formData, cgst_rate: parseFloat(e.target.value) || 0 })}
                        placeholder="e.g., 9"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="sgst_rate">{t('masters.tax.sgstRate')}</Label>
                      <Input
                        id="sgst_rate"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.sgst_rate || ''}
                        onChange={(e) => setFormData({ ...formData, sgst_rate: parseFloat(e.target.value) || 0 })}
                        placeholder="e.g., 9"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="igst_rate">{t('masters.tax.igstRate')}</Label>
                      <Input
                        id="igst_rate"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.igst_rate || ''}
                        onChange={(e) => setFormData({ ...formData, igst_rate: parseFloat(e.target.value) || 0 })}
                        placeholder="e.g., 18"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="utgst_rate">{t('masters.tax.utgstRate')}</Label>
                      <Input
                        id="utgst_rate"
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.utgst_rate || ''}
                        onChange={(e) => setFormData({ ...formData, utgst_rate: parseFloat(e.target.value) || 0 })}
                        placeholder="e.g., 9"
                      />
                    </div>
                  </div>
                )}

                <div className="grid gap-2">
                  <Label htmlFor="remarks">{t('common.remarks')}</Label>
                  <Textarea
                    id="remarks"
                    value={formData.remarks || ''}
                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    placeholder={t('masters.tax.optionalNotes')}
                    rows={3}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending
                    ? t('common.saving')
                    : editingTax
                    ? t('masters.tax.updateTax')
                    : t('masters.tax.createTax')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center -mb-1">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none bg-white dark:bg-neutral-800 rounded-sm">
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
          <Input
            placeholder={t('masters.tax.searchPlaceholder')}
            value={filters.searchTerm}
            onChange={(e) => {
              setFilters({ ...filters, searchTerm: e.target.value })
              setCurrentPage(1)
            }}
            className="pl-9"
          />
        </div>

        <Select
          value={filters.type}
          onValueChange={(value) => {
            setFilters({ ...filters, type: value })
            setCurrentPage(1)
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder={t('masters.tax.allTypes')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_TYPES}>{t('masters.tax.allTypes')}</SelectItem>
            <SelectItem value="gst">GST</SelectItem>
            <SelectItem value="standard">{t('masters.tax.standard')}</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={filters.status}
          onValueChange={(value) => {
            setFilters({ ...filters, status: value })
            setCurrentPage(1)
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder={t('common.allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>{t('common.allStatuses')}</SelectItem>
            <SelectItem value="active">{t('common.active')}</SelectItem>
            <SelectItem value="inactive">{t('common.inactive')}</SelectItem>
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="mr-1 h-4 w-4" />
            {t('common.reset')}
          </Button>
        )}
      </div>

      <TaxTable
        taxes={paginatedTaxes}
        allTaxes={taxes}
        filteredTaxes={filteredTaxes}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={(id) => deleteMutation.mutate(id)}
        isDeleting={deleteMutation.isPending}
        clearFilters={clearFilters}
        pageSize={pageSize}
        currentPage={currentPage}
        totalRecords={totalRecords}
        totalPages={totalPages}
        startRecord={startRecord}
        endRecord={endRecord}
        onPageSizeChange={handlePageSizeChange}
        onNextPage={goToNextPage}
        onPreviousPage={goToPreviousPage}
        t={t}
        canAddEdit={canAddEdit('TAX_MASTER')}
      />
    </div>
  )
}

function TaxTable({
  taxes,
  allTaxes,
  filteredTaxes,
  isLoading,
  onEdit,
  onDelete,
  isDeleting,
  clearFilters,
  pageSize,
  currentPage,
  totalRecords,
  totalPages,
  startRecord,
  endRecord,
  onPageSizeChange,
  onNextPage,
  onPreviousPage,
  t,
  canAddEdit,
}: {
  taxes: TaxMasterListItem[]
  allTaxes: TaxMasterListItem[]
  filteredTaxes: TaxMasterListItem[]
  isLoading: boolean
  onEdit: (tax: TaxMasterListItem) => void
  onDelete: (id: string) => void
  isDeleting: boolean
  clearFilters: () => void
  pageSize: number
  currentPage: number
  totalRecords: number
  totalPages: number
  startRecord: number
  endRecord: number
  onPageSizeChange: (size: string) => void
  onNextPage: () => void
  onPreviousPage: () => void
  t: (key: string) => string
  canAddEdit: boolean
}) {
  if (isLoading) {
    return <div className="text-center py-8">{t('common.loading')}</div>
  }

  if (allTaxes.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {t('masters.tax.noTaxesFound')}
        </CardContent>
      </Card>
    )
  }

  if (filteredTaxes.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="text-muted-foreground mb-4">
            {t('masters.tax.noMatchingTaxes')}
          </div>
          <Button variant="outline" onClick={clearFilters}>
            <X className="mr-2 h-4 w-4" />
            {t('common.reset')} {t('common.filters')}
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex flex-col h-[calc(100vh-280px)]">
      <Card className="flex-1 overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10">
              <TableRow>
                <TableHead>{t('masters.tax.taxName')}</TableHead>
                <TableHead>{t('masters.tax.type')}</TableHead>
                <TableHead>{t('masters.tax.rateComponents')}</TableHead>
                <TableHead>{t('common.remarks')}</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead className="w-[120px]">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {taxes.map((tax) => (
                <TableRow key={tax.tax_id}>
                  <TableCell className="font-medium">{tax.tax_name}</TableCell>
                  <TableCell>
                    {tax.is_gst ? (
                      <Badge variant="secondary">GST</Badge>
                    ) : (
                      <Badge variant="outline">{t('masters.tax.standard')}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {tax.is_gst ? (
                      <div className="text-sm space-y-1">
                        <div>CGST: {tax.cgst_rate}% | SGST: {tax.sgst_rate}%</div>
                        <div>IGST: {tax.igst_rate}% | UTGST: {tax.utgst_rate}%</div>
                      </div>
                    ) : (
                      <span>{tax.tax_rate}%</span>
                    )}
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate">
                    {tax.remarks || '-'}
                  </TableCell>
                  <TableCell>
                    {tax.is_active ? (
                      <Badge variant="default">{t('common.active')}</Badge>
                    ) : (
                      <Badge variant="secondary">{t('common.inactive')}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {canAddEdit && (
                          <>
                            <DropdownMenuItem onClick={() => onEdit(tax)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              {t('common.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => onDelete(tax.tax_id)}
                              disabled={isDeleting}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {t('common.delete')}
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {/* Pagination Controls */}
        <div className="flex items-center gap-3 px-4 py-2 border-t">
          <Select value={String(pageSize)} onValueChange={onPageSizeChange}>
            <SelectTrigger className="w-[70px] h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">
            {startRecord}-{endRecord} {t('common.of')} {totalRecords}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onPreviousPage}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onNextPage}
              disabled={currentPage >= totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}

